import { PROVIDER_CONNECTION_STATUSES, ROUTES } from 'Utils/constants';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, createContext, useMemo } from 'react';
import OutlineDashboard from 'Components/Atoms/OutlineIcons/OutlineDashboard';
import OutlineForecast from 'Components/Atoms/OutlineIcons/OutlineForecast';
import OutlineInvoice from 'Components/Atoms/OutlineIcons/OutlineInvoice';
import OutlineBank from 'Components/Atoms/OutlineIcons/OutlineBank';
import OutlineTag from 'Components/Atoms/OutlineIcons/OutlineTag';
import OutlineContacts from 'Components/Atoms/OutlineIcons/OutlineContacts';
import OutlineIntegration from 'Components/Atoms/OutlineIcons/OutlineIntegration';
import OutlineTransactions from 'Components/Atoms/OutlineIcons/OutlineTransactions';
import Icon from 'Components/Atoms/Icon/Icon';
import PropTypes from 'prop-types';
import useSettingsStorage from 'Hooks/useSettingsStorage';
import OutlineCalendar from 'Components/Atoms/OutlineIcons/OutlineCalendar';
import OutlineRecurrences from 'Components/Atoms/OutlineIcons/OutlineRecurrences';
import OutlineScan from 'Components/Atoms/OutlineIcons/OutlineScan';
import OutlineTaxes from 'Components/Atoms/OutlineIcons/OutlineTaxes';
import useInvoicesCountPendingValidation from 'Hooks/useInvoicesCountPendingValidation';
import useInvoicesCountDraft from 'Hooks/useInvoicesCountDraft';
import useInvoicesCountWithErrors from 'Hooks/useInvoicesCountWithErrors';
import useContactDuplicatedSuggestionsCount from 'Hooks/useContactDuplicatedSuggestionsCount';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import useConnectorsIncidents from 'Hooks/useConnectorsIncidents';
import useIntegrations from 'Hooks/useIntegrations';
import OutlineReconciliation from 'Components/Atoms/OutlineIcons/OutlineReconciliation';
import useFirstSteps from 'Hooks/useFirstSteps';

export const AppLayoutContext = createContext(null);

function calculateDividerHidden(groupItems, hiddenItems) {
    const visibleItems = groupItems.filter((itemId) => !hiddenItems.includes(itemId));
    return visibleItems.length <= 1;
}

export default function AppLayoutProvider({ children }) {
    const { pathname } = useLocation();

    const { settings, setSetting } = useSettingsStorage();

    const [hiddenItems, setHiddenItems] = useState([]);
    const { invoicesCount: pendingValidationCount } = useInvoicesCountPendingValidation();
    const { invoicesCount: invoicesDraftCount } = useInvoicesCountDraft();
    const { invoicesCount: invoicesWithErrorsCount } = useInvoicesCountWithErrors();
    const { count: duplicatedContactsCount } = useContactDuplicatedSuggestionsCount();
    const { isFirstStepsHidden } = useFirstSteps();

    const { providersConnections: allProvidersConnections } = useProvidersConnections();
    const { isConnectorBroken } = useConnectorsIncidents();

    // Connections of a bank that is down cannot be fixed by the user, so they are not counted.
    const providersConnections = useMemo(
        () => allProvidersConnections.filter((item) => !isConnectorBroken(item.connector)),
        [allProvidersConnections, isConnectorBroken],
    );

    const expiredProvidersCount = useMemo(() => {
        return providersConnections
            .filter((item) => [PROVIDER_CONNECTION_STATUSES.EXPIRED].includes(item.status))
            .filter((item) => !item.is_manual_sync)
            .filter((item) => item.products.some((product) => !product.is_hidden && !product.is_deleted))
            .filter((item) => !item.is_premium_blocked).length;
    }, [providersConnections]);

    const synchronizingErrorCount = useMemo(() => {
        return providersConnections.filter((item) =>
            [
                PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING_ERROR,
                PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING_ERROR_OTP,
                PROVIDER_CONNECTION_STATUSES.SYNCHRONIZING_ERROR_EXPIRED,
            ].includes(item.status),
        ).length;
    }, [providersConnections]);

    const banksBadgeCount = useMemo(() => {
        if (expiredProvidersCount > 0) {
            return expiredProvidersCount;
        }
        if (synchronizingErrorCount > 0) {
            return synchronizingErrorCount;
        }
        return null;
    }, [expiredProvidersCount, synchronizingErrorCount]);

    const invoicesBadgeCount = useMemo(() => {
        if (invoicesWithErrorsCount > 0) {
            return invoicesWithErrorsCount;
        }
        if (invoicesDraftCount > 0) {
            return invoicesDraftCount;
        }
        return null;
    }, [invoicesWithErrorsCount, invoicesDraftCount]);

    const { integrations } = useIntegrations();

    const expiredIntegrationsCount = useMemo(() => {
        return integrations.filter((item) => item.status === 'EXPIRED').length;
    }, [integrations]);

    const warningIntegrationsCount = useMemo(() => {
        return integrations.filter((item) => ['SYNCHRONIZING_ERROR', 'CREATING'].includes(item.status)).length;
    }, [integrations]);

    const integrationsBadgeCount = useMemo(() => {
        if (expiredIntegrationsCount > 0) {
            return expiredIntegrationsCount;
        }
        if (warningIntegrationsCount > 0) {
            return warningIntegrationsCount;
        }
        return null;
    }, [expiredIntegrationsCount, warningIntegrationsCount]);

    useEffect(() => {
        if (typeof settings?.hidden_navbar_items?.value === 'string') {
            setHiddenItems(JSON.parse(settings?.hidden_navbar_items?.value));
        } else {
            setHiddenItems([]);
        }
    }, [settings?.hidden_navbar_items?.value]);

    const { t } = useTranslation('common');

    const allNavbarItems = [
        {
            type: 'item',
            id: 'first-steps',
            label: t('first-steps'),
            Icon: (props) => <Icon name="nav-first-steps" {...props} />,
            route: ROUTES.firstSteps,
            className: 'text-primary hover:text-primary font-semibold',
            isCurrent: pathname.startsWith(ROUTES.firstSteps),
            hidden: false,
        },
        {
            type: 'item',
            id: 'dashboards',
            label: t('dashboards'),
            Icon: OutlineDashboard,
            route: ROUTES.dashboards,
            isCurrent: pathname.startsWith(ROUTES.dashboards),
            hidden: hiddenItems.includes('dashboards'),
        },
        {
            type: 'item',
            id: 'forecasts',
            label: t('scenarios'),
            Icon: OutlineForecast,
            route: ROUTES.forecasts,
            isCurrent: pathname.startsWith(ROUTES.forecasts),
            hidden: hiddenItems.includes('forecasts'),
        },
        {
            type: 'item',
            id: 'taxes',
            label: t('taxes'),
            Icon: OutlineTaxes,
            route: ROUTES.taxes,
            isCurrent: pathname === ROUTES.taxes,
            hidden: hiddenItems.includes('taxes'),
        },
        {
            type: 'divider',
            id: 'divider-tools',
            label: t('tools'),
            hidden: calculateDividerHidden(['scanInvoices', 'calendar', 'reconciliation', 'tagging'], hiddenItems),
        },
        {
            type: 'item',
            id: 'scanInvoices',
            label: t('scanner'),
            Icon: OutlineScan,
            route: ROUTES.scanInvoices,
            isCurrent: pathname.startsWith(ROUTES.scanInvoices),
            hidden: hiddenItems.includes('scanInvoices'),
            badgeCount: pendingValidationCount > 0 ? pendingValidationCount : null,
        },
        {
            type: 'item',
            id: 'calendar',
            label: t('calendar'),
            Icon: OutlineCalendar,
            route: ROUTES.calendar,
            isCurrent: pathname === ROUTES.calendar,
            hidden: hiddenItems.includes('calendar'),
        },
        {
            type: 'item',
            id: 'reconciliation',
            label: t('reconciliation'),
            Icon: OutlineReconciliation,
            route: ROUTES.reconciliation,
            isCurrent: pathname === ROUTES.reconciliation,
            hidden: hiddenItems.includes('reconciliation'),
        },
        {
            type: 'item',
            id: 'tagging',
            label: t('tagging'),
            Icon: OutlineTag,
            route: ROUTES.tagging,
            isCurrent: pathname === ROUTES.tagging,
            hidden: hiddenItems.includes('tagging'),
        },
        {
            type: 'divider',
            id: 'divider-records',
            label: t('records'),
            hidden: calculateDividerHidden(['transactions', 'invoices', 'recurrences', 'contacts'], hiddenItems),
        },
        {
            type: 'item',
            id: 'transactions',
            label: t('transactions'),
            Icon: OutlineTransactions,
            route: ROUTES.search,
            isCurrent: pathname.startsWith(ROUTES.search) || pathname === ROUTES.alerts,
            hidden: hiddenItems.includes('transactions'),
        },
        {
            type: 'item',
            id: 'invoices',
            label: t('invoices'),
            Icon: OutlineInvoice,
            route: ROUTES.invoices,
            isCurrent: pathname.startsWith(ROUTES.invoices),
            hidden: hiddenItems.includes('invoices'),
            badgeCount: invoicesBadgeCount,
            badgeVariant: invoicesWithErrorsCount > 0 ? 'danger' : undefined,
        },
        {
            type: 'item',
            id: 'recurrences',
            label: t('recurrences'),
            Icon: OutlineRecurrences,
            route: ROUTES.recurrences,
            isCurrent: pathname === ROUTES.recurrences,
            hidden: hiddenItems.includes('recurrences'),
        },
        {
            type: 'item',
            id: 'contacts',
            label: t('contacts'),
            Icon: OutlineContacts,
            route: ROUTES.contacts,
            isCurrent: pathname === ROUTES.contacts,
            hidden: hiddenItems.includes('contacts'),
            badgeCount: duplicatedContactsCount > 0 ? duplicatedContactsCount : null,
            badgeVariant: duplicatedContactsCount > 0 ? 'danger' : undefined,
        },
    ];

    const navbarItems = allNavbarItems.filter((item) => {
        if (item.id === 'first-steps') {
            return !isFirstStepsHidden;
        }
        return true;
    });

    const configurationItems = [
        {
            type: 'item',
            id: 'banks',
            label: t('banks'),
            Icon: OutlineBank,
            route: ROUTES.providers,
            badgeCount: banksBadgeCount,
            badgeVariant: expiredProvidersCount > 0 ? 'danger' : 'warning',
        },
        {
            type: 'item',
            id: 'integrations',
            label: t('integrations'),
            Icon: OutlineIntegration,
            route: ROUTES.integrations,
            badgeCount: integrationsBadgeCount,
            badgeVariant: expiredIntegrationsCount > 0 ? 'danger' : 'warning',
        },
        {
            type: 'item',
            id: 'categories',
            label: t('categories'),
            Icon: OutlineTag,
            route: ROUTES.categories,
        },
        {
            type: 'item',
            id: 'settings',
            label: t('settings'),
            Icon: (props) => <Icon name="gear" {...props} />,
            route: ROUTES.settings,
        },
    ];

    const value = {
        configurationItems,
        navbarItems,
        onCancel: () => {
            if (typeof settings?.hidden_navbar_items?.value === 'string') {
                setHiddenItems(JSON.parse(settings?.hidden_navbar_items?.value));
            } else {
                setHiddenItems([]);
            }
        },
        onSave: () => {
            setSetting({
                field: 'hidden_navbar_items',
                value: JSON.stringify(hiddenItems),
                is_global: false,
            });
        },
        onToogleItemHide: (itemId) => {
            setHiddenItems((pre) => {
                if (pre.includes(itemId)) {
                    return pre.filter((id) => id !== itemId);
                }

                return [...pre, itemId];
            });
        },
    };
    return <AppLayoutContext.Provider value={value}>{children}</AppLayoutContext.Provider>;
}

AppLayoutProvider.propTypes = {
    children: PropTypes.node,
};
